// `compactGraph`, ported from the prototype endpoint layer (`companion/src/serve.mjs:107-135` at
// Basil commit b9b62c8, served on `GET /api/episode` as `graph`). SHIP-SPEC-2026-09-05 section 4:
// the modal's preview panel and `onNavigateToNode` both need the scene-to-branch lookup, and U4
// names this the key gap in the surface port. It lives in the core rather than on the surface
// because amendment A5 allows exactly one implementation of anything the core can express.
//
// It is a projection, not a model: a compiled node list reduced to the fields a reader can be shown
// or navigated by, with every optional field defaulted so a consumer never has to test for absence.
// The rewrite from JavaScript is types only — the `??` chain of every field is the frozen one, in
// the frozen order, including `answerPrice ?? price` on a hub option (Basil's own two spellings,
// decompile.ts reads both) and the `Number(...)` coercion on both prices.
import type {
  Branch,
  EngineId,
  StepAction,
  StepCheck,
  SwitchArm,
} from '../../../dorian-shared/types/episodeScript';

/** One answer of a compact step: what a reader sees on a choice, and where it goes. */
export interface CompactAnswer {
  id: EngineId | undefined;
  text: string;
  price: number;
  gotoBranchId: EngineId | null;
}

/** One hub option of a compact step. */
export interface CompactHubOption {
  id: EngineId | undefined;
  title: string;
  price: number;
  gotoBranchId: EngineId | null;
}

/** One step of a compact node. */
export interface CompactStep {
  id: EngineId;
  stepTypeId: number;
  characterId: EngineId | null;
  characterExpressionId: EngineId | null;
  answers: CompactAnswer[];
  check: StepCheck | null;
  switch: SwitchArm[];
  action: StepAction | null;
  hubOptions: CompactHubOption[];
}

/** One node of the compact graph. */
export interface CompactNode {
  id: EngineId;
  title: string;
  locationId: EngineId | null;
  gotoBranchId: EngineId | null;
  steps: CompactStep[];
}

/**
 * The compiled node list as the compact graph the surface reads: one row per node, one row per
 * step, and only the fields a preview or a navigation needs. `title` falls back to `Node <id>` so
 * an untitled node is still nameable, and every list is present even when the node carries none.
 */
export function compactGraph(nodes: Branch[]): CompactNode[] {
  return nodes.map((node) => ({
    id: node.id,
    title: node.title ?? `Node ${node.id}`,
    locationId: node.locationId ?? null,
    gotoBranchId: node.gotoBranchId ?? null,
    steps: (node.steps ?? []).map((step) => ({
      id: step.id,
      stepTypeId: step.stepTypeId,
      characterId: step.characterId ?? null,
      characterExpressionId: step.characterExpressionId ?? null,
      answers: (step.answers ?? []).map((answer) => ({
        id: answer.id,
        text: answer.text ?? '',
        price: Number(answer.answerPrice ?? 0),
        gotoBranchId: answer.gotoBranchId ?? null,
      })),
      check: step.check ?? null,
      switch: step.switch ?? [],
      action: step.action ?? null,
      hubOptions: (step.hubOptions ?? []).map((option) => ({
        id: option.id,
        title: option.title ?? '',
        price: Number(option.answerPrice ?? option.price ?? 0),
        gotoBranchId: option.gotoBranchId ?? null,
      })),
    })),
  }));
}
